import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { useCategoryInit } from 'stores/categoryStore';
import Category from 'types/category';
import { getParentName } from 'utils/categoryUtils';
import SearchOverlay, { SearchOverlayHandle } from 'components/SearchOverlay';
import styles from './CategorySearchInput.module.scss';

interface CategorySearchInputProps {
  categories: { [key: string]: Category };
  value: string;
  onChange: (category: Category) => void;
  placeholder?: string;
  required?: boolean;
  excludeCategoryId?: string;
  disabled?: boolean;
  filter?: (category: Category) => boolean;
  enableDynamicCreation?: boolean;
  onCreateCategory?: (categoryName: string) => void;
  loading?: boolean;
}

function CategorySearchInput({
  categories,
  value,
  onChange,
  placeholder = 'Search for a category...',
  excludeCategoryId,
  disabled = false,
  filter,
  enableDynamicCreation = false,
  onCreateCategory,
  loading = false,
}: CategorySearchInputProps) {
  useCategoryInit();

  const [showOverlay, setShowOverlay] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const overlayRef = useRef<SearchOverlayHandle>(null);

  const selectedCategory = value ? categories[value] : null;
  const displayValue = selectedCategory ? selectedCategory.name : '';

  const filteredCategories = Object.values(categories).filter((category) => {
    if (excludeCategoryId && category.id === excludeCategoryId) {
      return false;
    }

    if (filter && !filter(category)) {
      return false;
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return category.name.toLowerCase().includes(query);
    }

    return true;
  });

  const exactCategoryExists = searchQuery.trim()
    ? filteredCategories.some(
        (category) =>
          category.name.toLowerCase() === searchQuery.trim().toLowerCase(),
      )
    : false;

  const showAddCategoryOption =
    enableDynamicCreation &&
    onCreateCategory &&
    searchQuery.trim() &&
    !exactCategoryExists;

  const totalItems =
    filteredCategories.length + (showAddCategoryOption ? 1 : 0);

  const handleClose = useCallback(() => {
    setShowOverlay(false);
  }, []);

  useEffect(() => {
    if (showOverlay) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  }, [showOverlay]);

  const handleSelect = (categoryId: string) => {
    overlayRef.current?.skipNextHistoryBack();
    onChange(categories[categoryId]);
    setShowOverlay(false);
  };

  const handleCreateCategory = () => {
    if (onCreateCategory && searchQuery.trim()) {
      onCreateCategory(searchQuery.trim());
      setSearchQuery('');
      setShowOverlay(false);
    }
  };

  const handleQueryChange = useCallback((q: string) => {
    setSearchQuery(q);
    setHighlightedIndex(-1);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (totalItems === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < totalItems - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (
            showAddCategoryOption &&
            highlightedIndex === filteredCategories.length
          ) {
            handleCreateCategory();
          } else if (highlightedIndex < filteredCategories.length) {
            handleSelect(filteredCategories[highlightedIndex].id);
          }
        } else if (filteredCategories.length === 1) {
          handleSelect(filteredCategories[0].id);
        } else if (showAddCategoryOption && filteredCategories.length === 0) {
          handleCreateCategory();
        }
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionsRef.current) {
      const el = suggestionsRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex]);

  return (
    <div className={styles.container}>
      <div
        role="combobox"
        aria-expanded={showOverlay}
        aria-haspopup="dialog"
        aria-controls="category-search-listbox"
        aria-label={placeholder}
        className={`${styles.triggerInput} ${disabled ? styles.triggerDisabled : ''}`}
        onClick={() => !disabled && setShowOverlay(true)}
      >
        <span
          className={
            displayValue ? styles.triggerValue : styles.triggerPlaceholder
          }
        >
          {displayValue || placeholder}
        </span>
        <ChevronDown className={styles.triggerIcon} size={18} />
      </div>

      <SearchOverlay
        ref={overlayRef}
        isOpen={showOverlay}
        onClose={handleClose}
        query={searchQuery}
        onQueryChange={handleQueryChange}
        placeholder="Search categories..."
        onKeyDown={handleKeyDown}
      >
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner} />
            <span>Loading categories...</span>
          </div>
        ) : (
          <>
            <ul
              ref={suggestionsRef}
              id="category-search-listbox"
              className={styles.optionsList}
              role="listbox"
              aria-label="Category options"
            >
              {filteredCategories.map((category, index) => {
                const parentName = getParentName(category, categories);
                return (
                  <li
                    key={category.id}
                    role="option"
                    aria-selected={value === category.id}
                    className={`${styles.optionItem} ${
                      index === highlightedIndex
                        ? styles.optionItemHighlighted
                        : ''
                    }`}
                    onClick={() => handleSelect(category.id)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <div className={styles.optionInfo}>
                      <span className={styles.optionName}>{category.name}</span>
                      {parentName && (
                        <span className={styles.optionParent}>
                          {parentName}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}

              {showAddCategoryOption && (
                <li
                  role="option"
                  aria-selected={highlightedIndex === filteredCategories.length}
                  className={`${styles.optionItem} ${styles.createOption} ${
                    highlightedIndex === filteredCategories.length
                      ? styles.optionItemHighlighted
                      : ''
                  }`}
                  onClick={handleCreateCategory}
                  onMouseEnter={() =>
                    setHighlightedIndex(filteredCategories.length)
                  }
                >
                  <Plus size={16} className={styles.createIcon} />
                  Add &quot;{searchQuery.trim()}&quot;
                </li>
              )}
            </ul>

            {searchQuery.trim() &&
              filteredCategories.length === 0 &&
              !showAddCategoryOption && (
                <div className={styles.noResults}>No categories found</div>
              )}
          </>
        )}
      </SearchOverlay>
    </div>
  );
}

export default CategorySearchInput;
